// The radio's vote card (docs/APP_REFERENCE.md "Radio"): above the dock, and left where it is while the interface
// idles, from OPEN_MS before a vote closes until the track it picks starts. Three buttons, one a style, each with its
// pixel icon (./style-icons.js) and its votes. voteView is pure (tests/vote.test.mjs); createVoteCard draws it.
import { OPEN_MS } from '../live/vote.js';
import { paintStyleIcon } from './style-icons.js';

const votes = n => `${n} ${n === 1 ? 'vote' : 'votes'}`;

/**
 * What the card shows at `now` (the radio's clock): { phase: 'hidden' } or { phase: 'open' | 'closed', left (whole
 * seconds to the close), options: [{ k, style, name, n, share, mine, won }], total, note }.
 * `vote` is the player's (null: none coming); `tally` the worker's { h, i, n, now } for a round (now: when it counted);
 * `mine` this page's { h, i, k, at, was } (at: when it voted, on the same clock; was: its earlier option in the round
 * that a tally has already counted, or null). The worker sends tallies at most every 10 s, so until one counted after
 * `at` (with a second for the trip), your own vote is added here and `was` taken off. Closed, it shows the winner
 * (vote.pick) until the track starts; a round nobody voted in hides as it closes (the show's style plays on).
 */
export function voteView(vote, now, { tally = null, mine = null } = {}) {
  if (!vote || now < vote.close - OPEN_MS || now >= vote.at) return { phase: 'hidden' };
  const same = x => !!x && x.h === vote.h && x.i === vote.i;
  const closed = now >= vote.close;
  if (closed && vote.pick === null) return { phase: 'hidden' };
  const n = same(tally) && Array.isArray(tally.n) ? vote.options.map((_, k) => Math.max(0, tally.n[k] | 0)) : vote.options.map(() => 0);
  const k = same(mine) ? mine.k : null;
  if (k !== null && !closed && !(same(tally) && tally.now >= mine.at + 1000) && mine.was !== k) {
    n[k]++;
    if (mine.was != null && n[mine.was] > 0) n[mine.was]--;
  }
  const total = n.reduce((a, b) => a + b, 0);
  const options = vote.options.map((o, j) => ({
    k: j, style: o.style, name: o.name, n: n[j], share: total ? n[j] / total : 0, mine: k === j, won: closed && vote.pick === j,
  }));
  const note = closed ? `${vote.options[vote.pick].name} plays next · ${votes(n[vote.pick])} of ${total}`
    : k === null ? 'pick the style of the next track' : 'your vote is in · you can change it until it closes';
  return { phase: closed ? 'closed' : 'open', left: Math.max(0, Math.ceil((vote.close - now) / 1000)), options, total, note };
}

/** The card in `el` (site/index.html #radioVote): render(view) draws a voteView, onPick(k) hears a button. */
export function createVoteCard(el, { onPick }) {
  const buttons = [...el.querySelectorAll('.rv-opt')], time = el.querySelector('.rv-time'), note = el.querySelector('.rv-note');
  const painted = buttons.map(() => null);          // the style each button's icon shows (a round's options stay put)
  buttons.forEach((b, k) => b.addEventListener('click', () => { if (!b.disabled) onPick(k); }));
  let shown = null;
  return {
    render(view) {
      el.hidden = view.phase === 'hidden';
      if (el.hidden) { shown = null; return; }
      el.classList.toggle('closed', view.phase === 'closed');
      time.textContent = view.phase === 'open' ? `${view.left} s` : 'closed';
      if (note.textContent !== view.note) note.textContent = view.note;   // aria-live: only real changes are read out
      view.options.forEach((o, k) => {
        const b = buttons[k];
        b.querySelector('.rv-name').textContent = o.name;
        if (painted[k] !== o.style) { paintStyleIcon(b.querySelector('.rv-ico'), o.style); painted[k] = o.style; }
        b.querySelector('.rv-n').textContent = o.n;
        b.style.setProperty('--share', o.share.toFixed(3));
        b.setAttribute('aria-pressed', String(o.mine));
        b.setAttribute('aria-label', `${o.name}, ${votes(o.n)}`);
        b.disabled = view.phase !== 'open';
        b.classList.toggle('won', o.won);
      });
      shown = view.phase;
    },
    get shown() { return shown; },
  };
}
